import { useLocation, useNavigate } from 'react-router-dom';

import { WithPopover, Wrapper } from '@american-autonomy/ag-ui';

import DataTableDropdownMenuButton from 'components/DataTableDropdownMenuButton';
import DataTableDropdownMenuItem from 'components/DataTableDropdownMenuItem';
import AccountIcon from 'components/icons/AccountIcon';
import ApplicationsIcon from 'components/icons/ApplicationsIcon';
import BoundaryIcon from 'components/icons/BoundaryIcon';
import DroneIcon from 'components/icons/DroneIcon';
import FarmsIcon from 'components/icons/FarmsIcon';
import FieldsIcon from 'components/icons/FieldsIcon';

import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { DataTablePagesMap, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const DataTableDropdown: FunctionComponent<Props> = ({ className = '', testId = TEST_ID }) => {
    const { APPLICATIONS, ASSETS, BOUNDARIES, CLIENTS, FARMS, FIELDS, USERS } = useTranslation();

    const {
        applicationsPage,
        assetsPage,
        boundariesPage,
        clientsPage,
        farmsPage,
        fieldsPage,
        usersPage
    } = usePageRoutes();
    const location = useLocation();
    const navigate = useNavigate();

    const dataTablePages: DataTablePagesMap = {
        [applicationsPage]: { Icon: ApplicationsIcon, label: APPLICATIONS },
        [assetsPage]: { Icon: DroneIcon, label: ASSETS },
        [boundariesPage]: { Icon: BoundaryIcon, label: BOUNDARIES },
        [clientsPage]: { Icon: AccountIcon, label: CLIENTS },
        [farmsPage]: { Icon: FarmsIcon, label: FARMS },
        [fieldsPage]: { Icon: FieldsIcon, label: FIELDS },
        [usersPage]: { Icon: AccountIcon, label: USERS }
    };

    const activeItem = dataTablePages[location.pathname] ?? dataTablePages[applicationsPage];

    const { Icon, label } = activeItem;

    return (
        <WithPopover
            trigger={
                <DataTableDropdownMenuButton
                    icon={<Icon className={styles.icon} />}
                    selectedItemText={label}
                />
            }
            className={className}
            testId={testId}>
            <Wrapper className={styles.wrapper}>
                {Object.entries(dataTablePages).map(([route, { Icon, label }]) => {
                    const isActive = location.pathname === route;
                    const activeStyles = isActive ? styles.active : '';

                    return (
                        <DataTableDropdownMenuItem
                            icon={<Icon className={`${activeStyles} ${styles.icon}`} />}
                            isActive={isActive}
                            key={route}
                            onClick={() => navigate(route)}
                            text={label}
                        />
                    );
                })}
            </Wrapper>
        </WithPopover>
    );
};

export default DataTableDropdown;
